import React from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch, useLocation, useRouteMatch } from 'react-router-dom';

import { withMaintenanceCheck } from '@/hocs';
import { DemoUIState } from '@/model/demo/types';
import { makeSelectFormData, makeSelectPhone, makeSelectStep, makeSelectTiming } from '@/model/withdrawal/selectors';
import { WithdrawalStep, WithdrawalUIState } from '@/model/withdrawal/types';
import { withWithdrawalInitialization } from '@/pages/withdrawal/hocs';
import { TransferPage } from '@/pages/withdrawal/pages';

import { withDemoInitialization } from './hocs';
import {
  PreparePage,
  ReceiverPage,
  SendOptionsPage,
  StatusPage,
  TransactionPage,
  TransactionsPage,
  WalletPage,
} from './pages';

const selectFormData = makeSelectFormData();
const selectPhone = makeSelectPhone();
const selectStep = makeSelectStep();
const selectTiming = makeSelectTiming();

const withRoutesValidation = (Component: React.FunctionComponent | React.ComponentClass, basePath: string) => (
  props: { [k in string]: unknown },
): JSX.Element => {
  const { pathname } = useLocation();
  const { step } = useSelector(selectStep);
  const formData = useSelector(selectFormData);
  const phone = useSelector(selectPhone);
  const timing = useSelector(selectTiming);
  if (
    // prettier-ignore
    // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
    pathname.endsWith(DemoUIState.WALLET)
    || pathname.endsWith(DemoUIState.SEND_OPTIONS)
    || pathname.match(new RegExp(`${DemoUIState.TRANSACTION}(\\/\\w*$)?`))
    || pathname.match(new RegExp(`${DemoUIState.TRANSACTIONS}(\\/\\d*$)?`))
  ) {
    return <Component {...props} />;
  }

  if ((!phone?.value || phone?.value !== formData?.receiver?.phone) && !pathname.endsWith(WithdrawalUIState.RECEIVER)) {
    return <Redirect to={`${basePath}/${WithdrawalUIState.RECEIVER}`} />;
  }
  if (
    // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
    // prettier-ignore
    (step === WithdrawalStep.Running || step === WithdrawalStep.Finished)
    && !pathname.endsWith(WithdrawalUIState.STATUS)
  ) {
    return <Redirect to={`${basePath}/${WithdrawalUIState.STATUS}`} />;
  }
  if (!formData && pathname.endsWith(WithdrawalUIState.STATUS)) {
    return <Redirect to={`${basePath}/${phone ? WithdrawalUIState.RECEIVER : WithdrawalUIState.PREPARE}`} />;
  }
  if (!timing && (pathname.endsWith(WithdrawalUIState.TRANSFER) || pathname.endsWith(WithdrawalUIState.PREPARE))) {
    return <Redirect to={`${basePath}/${WithdrawalUIState.RECEIVER}`} />;
  }
  return <Component {...props} />;
};

const DefaultRoute: React.FC<RouteComponentProps> = ({ location, match: { path } }: RouteComponentProps) => {
  console.warn(`URL not found: ${location.pathname}`);
  return <Redirect to={`${path}/${DemoUIState.WALLET}`} />;
};

export const DemoPages: React.FC = () => {
  const { path } = useRouteMatch();
  return (
    <Switch>
      <Route path={`${path}/${DemoUIState.WALLET}`} component={withRoutesValidation(WalletPage, path)} />
      <Route path={`${path}/${DemoUIState.SEND_OPTIONS}`} component={withRoutesValidation(SendOptionsPage, path)} />
      <Route path={`${path}/${DemoUIState.RECEIVER}`} component={withRoutesValidation(ReceiverPage, path)} />
      <Route path={`${path}/${DemoUIState.PREPARE}`} component={withRoutesValidation(PreparePage, path)} />
      <Route path={`${path}/${DemoUIState.TRANSFER}`} component={withRoutesValidation(TransferPage, path)} />
      <Route path={`${path}/${DemoUIState.STATUS}`} component={withRoutesValidation(StatusPage, path)} />
      <Route path={`${path}/${DemoUIState.TRANSACTION}/*`} component={withRoutesValidation(TransactionPage, path)} />
      <Route path={`${path}/${DemoUIState.TRANSACTIONS}`} component={withRoutesValidation(TransactionsPage, path)} />
      <Route path={`${path}/${DemoUIState.TRANSACTIONS}/*`} component={withRoutesValidation(TransactionsPage, path)} />
      <Route component={DefaultRoute} />
    </Switch>
  );
};

export default withMaintenanceCheck('demoDisabled')(
  withWithdrawalInitialization(false)(withDemoInitialization(DemoPages)),
);
